/**
 * The sentences this page builds out of a widget's own row.
 *
 * They are here, in one file, for the reason the whole page exists in one route:
 * `client/widget-chatbot.html` and `client/widget-booking.html` differ in their
 * unit noun, their numbers and their prose, and **nothing on this page may branch
 * on `widget_type`** (`.docs/13` — a new kind of widget is a `widget_type` row and
 * no code). Every string below is a template over `WidgetSummaryView`, so a third
 * widget type gets its sentences for free.
 *
 * What they are *not* is the mockups' own copy. Both pages open with an editorial
 * claim — "already more than the whole of July", "about one in six" — that no field
 * on `WidgetPageSchema` supports. Those are listed in the session report; what is
 * here is what the read model can say.
 *
 * **The noun rules live in `components/client/unitNoun.ts`**, not here, because
 * `WidgetUsagePanel` needs the same two and a component may not import from a page.
 * They are re-exported so this page still has one import for its own strings.
 */

import { sentenceCase, type WidgetSummaryView, type UnitNoun } from '@fareast/shared'

import { unitNounOf } from '../../../components/client/unitNoun.js'

export { pluralOf } from '../../../components/client/unitNoun.js'
export { sentenceCase }

/**
 * A unit noun as `formatQty` wants it.
 *
 * `unit_noun` is stored singular ("conversation", "booking") and the page needs
 * both. The `-s` plural is the same one `WidgetUsagePanel` applies — the same
 * function, now — and a noun that does not take it is a `widget_type` row to fix
 * rather than a branch to write.
 */
export function nounOf(summary: WidgetSummaryView): UnitNoun {
  return unitNounOf(summary.unit_noun)
}

/**
 * The kv row `client/widget-*.html` §1 prints as "Billing mode".
 *
 * A sentence per mode rather than the word on its own, because the word is not the
 * fact: what a client wants to know is *why* it is that way, and `mode` is derived
 * from whether they hold a live retainer and is not a setting anybody chose
 * (`packages/shared/src/schemas/usage.ts` `BillingModeSchema`).
 */
export const BILLING_MODE_SENTENCE = {
  postpay: 'Postpay — you hold an active retainer, so usage rides the monthly invoice',
  prepay: 'Prepay — you hold no retainer, so each event debits your prepaid balance',
} as const
